/**
 * Runtime configuration for the marketing + admin frontend.
 *
 * All values flow from VITE_* env vars baked in at build time. Nothing
 * brand- or domain-specific is hardcoded here so the same source can power
 * different deployments by changing env vars in the Cloudflare Pages
 * project (or wherever the frontend is hosted).
 *
 * Add new config in this file -- never reference `import.meta.env` from
 * components directly. That keeps the env var surface auditable.
 */

interface SiteConfig {
  /** Registered product display name. */
  brandName: string;
  /** Parent platform name shown beneath the wordmark, e.g. "JoatLabs.dev". */
  parentName: string;
  /** Parent platform URL (link from the parent line). */
  parentUrl: string;
  /** Public-facing contact email, e.g. "hello@example.io". */
  contactEmail: string;
  /** Marketing site canonical base URL with no trailing slash. */
  siteBaseUrl: string;
  /** App base URL used by public marketing CTAs that enter the product. */
  appBaseUrl: string;
  /** Tagline shown in HTML title and OG meta. */
  tagline: string;
  /** Optional one-paragraph site description used in meta tags. */
  description: string;
  /** Cloudflare Web Analytics token (renders the beacon when set). */
  analyticsToken: string | undefined;
  /** Cloudflare Turnstile site key (renders the widget when set). */
  turnstileSiteKey: string | undefined;
}

function trim(value: string | undefined): string {
  return (value ?? "").trim();
}

function trimRight(value: string | undefined, chars: string): string {
  let s = trim(value);
  while (s.length > 0 && chars.includes(s[s.length - 1])) {
    s = s.slice(0, -1);
  }
  return s;
}

export const config: SiteConfig = {
  brandName: trim(import.meta.env.VITE_BRAND_NAME) || "OrbitCell",
  parentName: trim(import.meta.env.VITE_PARENT_NAME) || "JoatLabs.dev",
  parentUrl:
    trimRight(import.meta.env.VITE_PARENT_URL, "/") || "https://joatlabs.dev",
  contactEmail: trim(import.meta.env.VITE_CONTACT_EMAIL),
  siteBaseUrl: trimRight(import.meta.env.VITE_SITE_BASE_URL, "/"),
  appBaseUrl: trimRight(import.meta.env.VITE_APP_BASE_URL, "/"),
  tagline:
    trim(import.meta.env.VITE_TAGLINE) ||
    "Development and automated testing for Ignition",
  description:
    trim(import.meta.env.VITE_SITE_DESCRIPTION) ||
    "Build and test Ignition projects in isolated environments before production, with current readiness and blockers visible.",
  analyticsToken: trim(import.meta.env.VITE_CF_ANALYTICS_TOKEN) || undefined,
  turnstileSiteKey: trim(import.meta.env.VITE_TURNSTILE_SITE_KEY) || undefined,
};
